import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Copy, Check, Globe, Sparkles, Mail, Save, Trash2, BellRing, FileText, Plus } from "lucide-react";
import { api } from "@/lib/api";

const auth = () => { const t = localStorage.getItem("fliboe_token"); return t ? { Authorization: `Bearer ${t}` } : {}; };
const EMAIL_RE = /[A-Za-z0-9._%+'-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const errMsg = (e, fb) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : fb; };
const FREQS = [0, 1, 2, 3, 5, 7, 14, 30];

export const parseEmails = (txt) => {
  const seen = new Set(); const out = [];
  (txt.match(EMAIL_RE) || []).forEach(e => { const k = e.toLowerCase(); if (!seen.has(k)) { seen.add(k); out.push(e); } });
  return out;
};

export function useCopy() {
  const [copied, setCopied] = useState("");
  const copy = (k, t) => { navigator.clipboard.writeText(t || ""); setCopied(k); setTimeout(() => setCopied(""), 1400); };
  const Btn = ({ k, text, label = "Copy" }) => (
    <button type="button" onClick={() => copy(k, text)} className="fl-btn ghost !py-1 !px-2 text-[11px]" data-testid={`amp-copy-${k}`}>
      {copied === k ? <><Check className="w-3 h-3 text-emerald-400" /> Copied</> : <><Copy className="w-3 h-3" /> {label}</>}
    </button>
  );
  return Btn;
}

export function SiteReader({ url, setUrl, site, setSite, testPrefix }) {
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const read = async () => {
    if (url.trim().length < 4) return;
    setBusy(true); setErr("");
    try { const { data } = await api.post("/ampliboe/fetch-url", { url }, { headers: auth(), timeout: 30000 }); setSite(data); }
    catch (e) { setErr(errMsg(e, "Couldn't read that link")); setSite(null); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Website or link (optional · free to read)</span>
      <div className="flex gap-2 mt-1">
        <input className="fl-input flex-1" value={url} onChange={e => { setUrl(e.target.value); setSite(null); setErr(""); }} placeholder="https://yourbrand.com or YouTube / Showo link" data-testid={`${testPrefix}-url`} />
        <button type="button" onClick={read} disabled={busy} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid={`${testPrefix}-read-url`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Globe className="w-3.5 h-3.5" />} Read
        </button>
      </div>
      {err && <div className="text-red-300 text-xs mt-1" data-testid={`${testPrefix}-url-err`}>{err}</div>}
      {site && (
        <div className="mt-2 p-2 rounded bg-emerald-500/10 border border-emerald-400/30 text-xs" data-testid={`${testPrefix}-site`}>
          <div className="text-emerald-300 font-semibold truncate">{site.title || site.url}</div>
          <div className="text-white/70 line-clamp-2">{site.description || site.excerpt?.slice(0, 160)}</div>
        </div>
      )}
    </div>
  );
}

function SendBatches({ recipients, subject, body }) {
  const [size, setSize] = useState(40);
  const [opened, setOpened] = useState({});
  const Copy = useCopy();
  const batches = useMemo(() => { const b = []; for (let i = 0; i < recipients.length; i += size) b.push(recipients.slice(i, i + size)); return b; }, [recipients, size]);
  const open = (i, kind) => {
    const bcc = batches[i].join(","); const s = encodeURIComponent(subject); const bd = encodeURIComponent(body);
    const url = kind === "gmail" ? `https://mail.google.com/mail/?view=cm&fs=1&bcc=${encodeURIComponent(bcc)}&su=${s}&body=${bd}`
      : kind === "outlook" ? `https://outlook.office.com/mail/deeplink/compose?bcc=${encodeURIComponent(bcc)}&subject=${s}&body=${bd}`
      : `mailto:?bcc=${encodeURIComponent(bcc)}&subject=${s}&body=${bd}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setOpened(o => ({ ...o, [i]: true }));
  };
  if (!recipients.length) return null;
  return (
    <div className="mt-3 space-y-2" data-testid="amp-email-batches">
      <div className="flex items-center gap-2 text-xs text-white/70 flex-wrap">
        <span>Send in batches of</span>
        <select className="fl-input !py-1 !w-auto" value={size} onChange={e => setSize(Number(e.target.value))} data-testid="amp-email-batch-size">
          {[10, 25, 40, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <span className="text-white/40">· everyone is BCC'd so addresses stay private</span>
      </div>
      {batches.map((b, i) => (
        <div key={i} className={`p-2 rounded border flex items-center gap-2 flex-wrap ${opened[i] ? "border-emerald-400/40 bg-emerald-500/5" : "border-white/10 bg-black/30"}`} data-testid={`amp-email-batch-${i}`}>
          <span className="text-xs text-white/80 flex-1 min-w-[120px]">Batch {i + 1} · {b.length} emails {opened[i] && <Check className="inline w-3 h-3 text-emerald-400" />}</span>
          <button type="button" onClick={() => open(i, "gmail")} className="fl-btn !bg-fuchsia-500 !text-black !py-1 !px-2 text-[11px]" data-testid={`amp-email-gmail-${i}`}>Gmail</button>
          <button type="button" onClick={() => open(i, "outlook")} className="fl-btn ghost !py-1 !px-2 text-[11px]" data-testid={`amp-email-outlook-${i}`}>Outlook</button>
          <button type="button" onClick={() => open(i, "mailto")} className="fl-btn ghost !py-1 !px-2 text-[11px]" data-testid={`amp-email-mailto-${i}`}>Mail app</button>
          <Copy k={`batch-${i}`} text={b.join(", ")} label="Emails" />
        </div>
      ))}
    </div>
  );
}

function Campaigns({ list, onLoad, onDelete, onRemind, reminding }) {
  if (!list.length) return null;
  return (
    <div className="mt-6" data-testid="amp-email-campaigns">
      <div className="fl-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">// Saved campaigns</div>
      <div className="space-y-1.5">
        {list.map(c => (
          <div key={c.campaign_id} className="p-2 rounded bg-white/[0.02] border border-white/10 text-xs flex items-center gap-2 flex-wrap" data-testid={`amp-campaign-${c.campaign_id}`}>
            <button type="button" onClick={() => onLoad(c)} className="flex-1 min-w-[140px] text-left" data-testid={`amp-campaign-load-${c.campaign_id}`}>
              <div className="text-fuchsia-300 font-semibold truncate">{c.name}</div>
              <div className="text-white/50">{c.recipients?.length || 0} recipients · {c.frequency_days ? `reminder every ${c.frequency_days}d · next ${new Date(c.next_due).toLocaleDateString()}` : "no reminder"}</div>
            </button>
            <button type="button" onClick={() => onRemind(c)} disabled={reminding === c.campaign_id} className="fl-btn ghost !py-1 !px-2 text-[11px]" data-testid={`amp-campaign-remind-${c.campaign_id}`}>
              {reminding === c.campaign_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <BellRing className="w-3 h-3" />} Email me now
            </button>
            <button type="button" onClick={() => onDelete(c)} className="fl-btn ghost !py-1 !px-2 text-[11px] !text-red-300" data-testid={`amp-campaign-del-${c.campaign_id}`}><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AmpliboeEmail({ subApps }) {
  const [templates, setTemplates] = useState([]);
  const [reminderTo, setReminderTo] = useState("");
  const [tplId, setTplId] = useState("");
  const [about, setAbout] = useState("");
  const [url, setUrl] = useState(""); const [site, setSite] = useState(null);
  const [subApp, setSubApp] = useState("");
  const [tone, setTone] = useState("warm, clear, friendly");
  const [length, setLength] = useState("short");
  const [subject, setSubject] = useState(""); const [preheader, setPreheader] = useState(""); const [body, setBody] = useState("");
  const [alts, setAlts] = useState([]);
  const [recText, setRecText] = useState("");
  const [name, setName] = useState(""); const [freq, setFreq] = useState(0); const [aiRefresh, setAiRefresh] = useState(false);
  const [campaignId, setCampaignId] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [busy, setBusy] = useState(""); const [err, setErr] = useState(""); const [note, setNote] = useState("");
  const Copy = useCopy();
  const recipients = useMemo(() => parseEmails(recText), [recText]);

  useEffect(() => {
    api.get("/ampliboe/email/templates").then(r => { setTemplates(r.data.templates || []); setReminderTo(r.data.reminder_to || ""); });
    api.get("/ampliboe/email/campaigns", { headers: auth() }).then(r => setCampaigns(r.data.campaigns || [])).catch(() => {});
  }, []);

  const applyTemplate = (t) => { setTplId(t.id); setSubject(t.subject); setBody(t.body); setPreheader(""); setAlts([]); };

  const write = async () => {
    setErr(""); setNote(""); setBusy("draft");
    try {
      const { data } = await api.post("/ampliboe/email/draft", { about, url: url || null, sub_app: subApp || null, template_id: tplId || null, tone, length, cta_link: site?.url || null }, { headers: auth(), timeout: 90000 });
      setSubject(data.subject); setPreheader(data.preheader); setBody(data.body); setAlts(data.alt_subjects || []);
      if (data.site) setSite(data.site);
    } catch (e) { setErr(errMsg(e, "AI draft failed")); }
    finally { setBusy(""); }
  };

  const save = async () => {
    if (!name.trim()) { setErr("Give the campaign a name to save it."); return; }
    setErr(""); setBusy("save");
    const payload = { name, subject, preheader, body, recipients, frequency_days: freq, ai_refresh: aiRefresh, about, url: url || null };
    try {
      const { data } = campaignId
        ? await api.put(`/ampliboe/email/campaigns/${campaignId}`, payload, { headers: auth() })
        : await api.post("/ampliboe/email/campaigns", payload, { headers: auth() });
      setCampaignId(data.campaign_id);
      setCampaigns(cs => [data, ...cs.filter(c => c.campaign_id !== data.campaign_id)]);
      setNote(freq ? `Saved. A ready draft will land in ${reminderTo} every ${freq} day${freq > 1 ? "s" : ""}.` : "Saved.");
    } catch (e) { setErr(errMsg(e, "Save failed")); }
    finally { setBusy(""); }
  };

  const load = (c) => {
    setCampaignId(c.campaign_id); setName(c.name); setSubject(c.subject); setPreheader(c.preheader); setBody(c.body);
    setRecText((c.recipients || []).join("\n")); setFreq(c.frequency_days || 0); setAiRefresh(!!c.ai_refresh);
    setAbout(c.about || ""); setUrl(c.url || ""); setNote(""); setErr("");
  };
  const del = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    await api.delete(`/ampliboe/email/campaigns/${c.campaign_id}`, { headers: auth() });
    setCampaigns(cs => cs.filter(x => x.campaign_id !== c.campaign_id));
    if (campaignId === c.campaign_id) setCampaignId(null);
  };
  const remind = async (c) => {
    setBusy(`remind-${c.campaign_id}`); setErr("");
    try { const { data } = await api.post(`/ampliboe/email/campaigns/${c.campaign_id}/remind-now`, {}, { headers: auth(), timeout: 60000 }); setNote(`Draft emailed to ${data.sent_to}.`); }
    catch (e) { setErr(errMsg(e, "Could not send reminder")); }
    finally { setBusy(""); }
  };

  return (
    <section className="max-w-6xl mx-auto px-5 py-8 grid lg:grid-cols-5 gap-6" data-testid="amp-email-section">
      <div className="lg:col-span-2 space-y-3">
        <div className="fl-mono text-[11px] uppercase tracking-widest text-fuchsia-400">// Email marketing</div>
        <h2 className="fl-display text-3xl font-black">Write it once. Send it in batches.</h2>
        <p className="text-slate-400 text-sm">Pick a template (free) or let AI write a draft (one small call). Paste as many emails as you like, then send each batch from your own Gmail or Outlook, with everyone in BCC.</p>
        <div>
          <span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Ready-to-use templates</span>
          <div className="mt-1 flex flex-wrap gap-1.5" data-testid="amp-email-templates">
            {templates.map(t => (
              <button key={t.id} type="button" onClick={() => applyTemplate(t)} className={`px-2 py-1 rounded-full border text-[11px] ${tplId === t.id ? "bg-fuchsia-500/20 border-fuchsia-400 text-white" : "border-white/15 text-white/70 hover:border-white/30"}`} data-testid={`amp-email-tpl-${t.id}`}>{t.label}</button>
            ))}
          </div>
        </div>
        <label className="block"><span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">What's the email about?</span>
          <textarea className="fl-input w-full mt-1 min-h-[80px]" value={about} onChange={e => setAbout(e.target.value)} placeholder="New music video 'Neon Rain' by Maya, out Friday on Showo" data-testid="amp-email-about" /></label>
        <SiteReader url={url} setUrl={setUrl} site={site} setSite={setSite} testPrefix="amp-email" />
        <div className="grid grid-cols-3 gap-2">
          <select className="fl-input" value={subApp} onChange={e => setSubApp(e.target.value)} data-testid="amp-email-subapp">
            <option value="">Any brand</option>
            {subApps.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select className="fl-input" value={length} onChange={e => setLength(e.target.value)} data-testid="amp-email-length">
            <option value="short">Short</option><option value="medium">Medium</option><option value="long">Long</option>
          </select>
          <input className="fl-input" value={tone} onChange={e => setTone(e.target.value)} data-testid="amp-email-tone" />
        </div>
        <button type="button" onClick={write} disabled={busy === "draft"} className="fl-btn !bg-fuchsia-500 !text-black w-full justify-center" data-testid="amp-email-write">
          {busy === "draft" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {busy === "draft" ? "Writing…" : "Write with AI"}
        </button>
        <Campaigns list={campaigns} onLoad={load} onDelete={del} onRemind={remind} reminding={busy.startsWith("remind-") ? busy.slice(7) : ""} />
      </div>

      <div className="lg:col-span-3 space-y-3">
        <div className="fl-card p-4 bg-white/[0.02] border-white/10 space-y-2" data-testid="amp-email-editor">
          <div className="flex items-center justify-between"><span className="fl-mono text-[10px] uppercase tracking-widest text-fuchsia-300">// Subject</span><Copy k="subject" text={subject} /></div>
          <input className="fl-input w-full" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject line" data-testid="amp-email-subject" />
          {alts.length > 0 && <div className="flex flex-wrap gap-1">{alts.map((a, i) => <button key={i} type="button" onClick={() => setSubject(a)} className="text-[11px] px-2 py-0.5 rounded bg-white/5 border border-white/10 text-white/70 hover:text-white" data-testid={`amp-email-alt-${i}`}>{a}</button>)}</div>}
          <input className="fl-input w-full text-sm" value={preheader} onChange={e => setPreheader(e.target.value)} placeholder="Preview text (optional)" data-testid="amp-email-preheader" />
          <div className="flex items-center justify-between pt-1"><span className="fl-mono text-[10px] uppercase tracking-widest text-fuchsia-300">// Body · replace any {"{placeholders}"}</span><Copy k="body" text={body} /></div>
          <textarea className="fl-input w-full min-h-[240px] text-sm leading-relaxed" value={body} onChange={e => setBody(e.target.value)} placeholder="Pick a template or hit Write with AI…" data-testid="amp-email-body" />
        </div>

        <div className="fl-card p-4 bg-white/[0.02] border-white/10" data-testid="amp-email-recipients-card">
          <div className="flex items-center justify-between"><span className="fl-mono text-[10px] uppercase tracking-widest text-fuchsia-300"><Mail className="w-3 h-3 inline" /> Recipients · paste any list</span>
            <span className="text-xs text-emerald-300" data-testid="amp-email-count">{recipients.length} valid · duplicates removed</span></div>
          <textarea className="fl-input w-full mt-2 min-h-[90px] text-xs" value={recText} onChange={e => setRecText(e.target.value)} placeholder="Paste emails separated by commas, spaces, new lines, or a whole spreadsheet column" data-testid="amp-email-recipients" />
          <SendBatches recipients={recipients} subject={subject} body={body} />
          <p className="text-[10px] text-white/40 mt-2">Only email people who agreed to hear from you. Add a line on how to opt out.</p>
        </div>

        <div className="fl-card p-4 bg-white/[0.02] border-white/10 space-y-2" data-testid="amp-email-save-card">
          <div className="fl-mono text-[10px] uppercase tracking-widest text-fuchsia-300"><FileText className="w-3 h-3 inline" /> Save & auto reminder</div>
          <div className="grid sm:grid-cols-2 gap-2">
            <input className="fl-input" value={name} onChange={e => setName(e.target.value)} placeholder="Campaign name" data-testid="amp-email-name" />
            <select className="fl-input" value={freq} onChange={e => setFreq(Number(e.target.value))} data-testid="amp-email-frequency">
              {FREQS.map(f => <option key={f} value={f}>{f ? `Email me this draft every ${f} day${f > 1 ? "s" : ""}` : "No auto reminder"}</option>)}
            </select>
          </div>
          {freq > 0 && (
            <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer" data-testid="amp-email-refresh-wrap">
              <input type="checkbox" checked={aiRefresh} onChange={e => setAiRefresh(e.target.checked)} data-testid="amp-email-refresh" />
              Write a fresh AI version each time (uses one small AI call per reminder)
            </label>
          )}
          <div className="text-[11px] text-white/50">Reminders go to <strong className="text-white/80">{reminderTo}</strong> with the ready draft, so you can send the batches.</div>
          {err && <div className="text-red-300 text-sm" data-testid="amp-email-err">{err}</div>}
          {note && <div className="text-emerald-300 text-sm" data-testid="amp-email-note">{note}</div>}
          <div className="flex gap-2">
            <button type="button" onClick={save} disabled={busy === "save"} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="amp-email-save">
              {busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {campaignId ? "Update campaign" : "Save campaign"}
            </button>
            {campaignId && (
              <button type="button" onClick={() => { setCampaignId(null); setName(""); setSubject(""); setPreheader(""); setBody(""); setRecText(""); setFreq(0); setAiRefresh(false); setTplId(""); setAlts([]); setNote(""); }} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="amp-email-new">
                <Plus className="w-3.5 h-3.5" /> New campaign
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
